import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, UploadCloud, X, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPES = ["T-Shirts", "Hoodies", "Team Jerseys", "Hats", "Drinkware", "Bags", "Promotional Gifts", "Other"];
const isImage = (ct) => ct && ct.startsWith("image/") && ct !== "image/svg+xml";

export default function Quote() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "T-Shirts", quantity: "", details: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setUploading(true);
    for (const file of arr) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
        toast.success(`Uploaded ${data.original_filename}`);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed. Please try again.";
        toast.error(msg);
      }
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quote", { ...form, files });
      setDone(true);
      toast.success("Quote request received! 🎉");
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", project_type: "T-Shirts", quantity: "", details: "" });
    setFiles([]);
  };

  return (
    <div data-testid="quote-page">
      <PageHero eyebrow="START YOUR PROJECT" title="GET A QUOTE" script="tell us your vision" />
      <section className="py-20 bg-paper">
        <div className="max-w-[760px] mx-auto px-5 lg:px-8">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-line p-12 text-center" data-testid="quote-success">
              <CheckCircle2 size={56} className="text-navy mx-auto mb-5" />
              <h2 className="font-display text-3xl mb-3">THANK YOU!</h2>
              <p className="text-navy/65">We've received your request{files.length ? " and your files" : ""} and will respond with a custom quote shortly. Keep an eye on your inbox.</p>
              <button onClick={reset} className="mt-8 text-sm tracking-[0.16em] font-semibold text-navy hover:text-gold transition-colors">SUBMIT ANOTHER REQUEST</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-line p-8 sm:p-10" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input required value={form.name} onChange={set("name")} placeholder="Full name *" data-testid="quote-name" className="border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" data-testid="quote-email" className="border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone" data-testid="quote-phone" className="border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
                <input value={form.quantity} onChange={set("quantity")} placeholder="Estimated quantity" data-testid="quote-quantity" className="border border-line px-4 py-3 outline-none focus:border-navy transition-colors" />
              </div>

              <div className="mt-5">
                <label className="text-xs tracking-[0.16em] text-navy/60 font-semibold">PROJECT TYPE</label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {TYPES.map((t) => (
                    <button type="button" key={t} onClick={() => setForm({ ...form, project_type: t })} data-testid={`quote-type-${t.replace(/\s/g, "-").toLowerCase()}`}
                      className={`px-4 py-2 text-sm border transition-colors ${form.project_type === t ? "bg-navy text-white border-navy" : "border-line text-navy hover:border-navy"}`}>{t}</button>
                  ))}
                </div>
              </div>

              <textarea rows={5} value={form.details} onChange={set("details")} placeholder="Tell us about your project — colors, sizes, artwork, deadline..." data-testid="quote-details" className="w-full mt-5 border border-line px-4 py-3 outline-none focus:border-navy transition-colors resize-none" />

              {/* Upload */}
              <div className="mt-6">
                <label className="text-xs tracking-[0.16em] text-navy/60 font-semibold">UPLOAD YOUR LOGO / DESIGN FILES</label>
                <div
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  data-testid="quote-dropzone"
                  className={`mt-3 border-2 border-dashed cursor-pointer text-center px-6 py-10 transition-colors ${dragging ? "border-navy bg-navy/5" : "border-line hover:border-navy/50"}`}
                >
                  <UploadCloud size={30} className="mx-auto text-navy/70 mb-3" />
                  <p className="text-sm text-navy/70">
                    <span className="font-semibold text-navy">Click to upload</span> or drag & drop
                  </p>
                  <p className="text-xs text-navy/45 mt-1">PNG, JPG, SVG, PDF, AI, EPS, PSD · up to 30MB each</p>
                  <input ref={inputRef} type="file" multiple hidden data-testid="quote-file-input"
                    accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.pdf,.ai,.eps,.psd"
                    onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </div>

                {uploading && (
                  <div className="flex items-center gap-2 text-sm text-navy/70 mt-3" data-testid="upload-progress">
                    <Loader2 size={16} className="animate-spin" /> Uploading…
                  </div>
                )}

                {files.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4" data-testid="uploaded-files">
                    {files.map((f) => (
                      <div key={f.id} className="relative border border-line group" data-testid={`uploaded-file-${f.id}`}>
                        <button type="button" onClick={() => removeFile(f.id)} aria-label="Remove file" data-testid={`remove-file-${f.id}`}
                          className="absolute top-1 right-1 z-10 bg-ink text-white w-6 h-6 flex items-center justify-center opacity-80 hover:opacity-100">
                          <X size={13} />
                        </button>
                        <div className="aspect-square bg-[#e9e6dd] flex items-center justify-center overflow-hidden">
                          {isImage(f.content_type)
                            ? <img src={`${BACKEND}${f.url}`} alt={f.original_filename} className="w-full h-full object-cover" />
                            : <FileText size={34} className="text-navy/50" />}
                        </div>
                        <div className="px-2 py-1.5 text-[11px] text-navy/70 truncate">{f.original_filename}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading || uploading} data-testid="quote-submit" className="w-full mt-7 bg-ink text-[#f2f0ea] py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-navy transition-colors disabled:opacity-60">
                {loading ? "SENDING..." : "REQUEST MY QUOTE"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
